import { z } from 'zod';

// AUT-39: WEB-002. One ordered list of password rules -- the single source
// the ADR requires (decisions/password-policy-has-one-source-in-the-spa.md).
// `resetPasswordSchema` and `signUpSchema` both derive their `password`
// field from this array via `passwordPolicySchema` below, and
// `/reset-password`'s live checklist (WEB-004) renders from the same array,
// so a change to the policy is one edit that moves all three. `test` is a
// predicate on every rule -- never a number on one and a `RegExp` on the
// others -- so a consumer can evaluate any rule without knowing which rule
// it holds. `id` also regenerates today's
// `password-requirement-length|number|symbol` test ids.
export interface PasswordRule {
  id: string;
  label: string;
  test: (value: string) => boolean;
  message: string;
}

// WEB-008: messages read directly off the live prototype (correction sheet,
// 2026-08-17) -- labels are unchanged and deliberately differ from these
// messages (labels are the checklist's own copy; messages are the resolver's
// blocking text).
export const passwordRules: PasswordRule[] = [
  {
    id: 'length',
    label: 'At least 8 characters',
    test: (value) => value.length >= 8,
    message: 'Use at least 8 characters.',
  },
  {
    id: 'number',
    label: 'Contains a number',
    test: (value) => /[0-9]/.test(value),
    message: 'Include at least one number.',
  },
  {
    id: 'symbol',
    label: 'Contains a symbol',
    test: (value) => /[^A-Za-z0-9]/.test(value),
    message: 'Include at least one symbol.',
  },
];

// The one derivation both schemas compose. A `superRefine` walking
// `passwordRules` in order, so a rule's failure surfaces that rule's own
// `message` and, on a value that fails several rules, the first rule in
// array order is the first issue -- which is what `zodResolver` displays --
// exactly as the previous `.min(8).regex().regex()` chain did.
const passwordPolicySchema = z.string().superRefine((value, ctx) => {
  passwordRules.forEach((rule) => {
    if (!rule.test(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: rule.message });
    }
  });
});

// WEB-008: overturns FR-7. FR-7 narrowed loginSchema to one message
// covering both the empty and the malformed email case; the live prototype
// has two distinct messages, and the human asked for literal matching over
// the FR. "Enter your email address." is the empty case; "Enter a valid
// email address." is the syntactically-invalid, non-empty case.
// "Enter your password." is unchanged.
export const loginSchema = z.object({
  email: z.string().min(1, 'Enter your email address.').email('Enter a valid email address.'),
  password: z.string().min(1, 'Enter your password.'),
  remember: z.boolean().default(false),
});

// WEB-008: same FR-7 reversal as loginSchema above -- two distinct email
// messages, matching the prototype.
export const forgotPasswordSchema = z.object({
  email: z.string().min(1, 'Enter your email address.').email('Enter a valid email address.'),
});

// AUT-39: WEB-002. Mirrors business rule 39 (`ResetPasswordRequest`) -- >=8
// chars, >=1 digit, >=1 symbol -- via `passwordPolicySchema` above. The
// messages here are the resolver's blocking mechanism; the screen surfaces
// the same three conditions live as the checklist
// (components/account.md § business rule 39), not as this text.
export const resetPasswordSchema = z.object({
  password: passwordPolicySchema,
  // WEB-008: "Re-enter your new password." -- prototype's exact string
  // (was "Confirm your new password.").
  password_confirmation: z.string().min(1, "Re-enter your new password."),
}).refine(
  (data) => data.password === data.password_confirmation,
  {
    message: "Passwords don't match.",
    path: ['password_confirmation'],
  },
);

// AUT-39: WEB-002. `/signup`'s single password field, built from the same
// `passwordPolicySchema` as `resetPasswordSchema` -- one policy, two
// schemas, per decisions/password-policy-has-one-source-in-the-spa.md.
// `/signup` has no confirm field (FR-11), so this schema has no
// `password_confirmation` field and no object-level `.refine()`.
export const signUpSchema = z.object({
  first_name: z.string().min(1, 'Enter your first name.'),
  last_name: z.string().min(1, 'Enter your last name.'),
  company: z.string().min(1, 'Enter your company name.'),
  // WEB-008: same FR-7 reversal as loginSchema -- two distinct email
  // messages, matching the prototype.
  email: z.string().min(1, 'Enter your email address.').email('Enter a valid email address.'),
  password: passwordPolicySchema,
});

// Update Password / Accept Invitation policy: 8+ chars, upper, lower, digit.
// Same single-source pattern as `passwordRules` above: the schema derives from
// this array and Accept Invitation's live checklist renders from it. Labels
// match the checklist on Update Password (components/settings/PasswordTab.tsx).
export const mixedCasePasswordRules: PasswordRule[] = [
  {
    id: 'length',
    label: 'At least 8 characters',
    test: (value) => value.length >= 8,
    message: 'Password must be at least 8 characters',
  },
  {
    id: 'uppercase',
    label: 'At least one uppercase letter',
    test: (value) => /[A-Z]/.test(value),
    message: 'Password must contain an uppercase letter',
  },
  {
    id: 'lowercase',
    label: 'At least one lowercase letter',
    test: (value) => /[a-z]/.test(value),
    message: 'Password must contain a lowercase letter',
  },
  {
    id: 'number',
    label: 'At least one number',
    test: (value) => /[0-9]/.test(value),
    message: 'Password must contain a number',
  },
];

export const mixedCasePasswordSchema = z.string().superRefine((value, ctx) => {
  mixedCasePasswordRules.forEach((rule) => {
    if (!rule.test(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: rule.message });
    }
  });
});

export const updatePasswordSchema = z.object({
  current_password: z.string().min(1, 'Current password is required'),
  password: mixedCasePasswordSchema,
  password_confirmation: z.string().min(1, 'Please confirm your password'),
}).refine(
  (data) => data.password === data.password_confirmation,
  {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  },
);
